// src/components/DApp/CombinedConverter.tsx
import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Slider from "@radix-ui/react-slider";
import logoYes from "../../assets/img/Token_yes.webp";
import logoNo from "../../assets/img/Token_no.webp";

export interface CombinedConverterProps {
  conversionRatio: number;
  onRatioChange: (ratio: number) => void;
  loading: boolean;
  convertedAmountNAUY: number;
  convertedAmountNAUN: number;
  error?: string | null;
}

// Animation variants for list items
const rowVariants = {
  hidden: { opacity: 0, y: -5 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, stiffness: 120 },
  }),
};

const CombinedConverter: React.FC<CombinedConverterProps> = ({
  conversionRatio,
  onRatioChange,
  loading,
  convertedAmountNAUY,
  convertedAmountNAUN,
  error,
}) => {
  // Track slider hover percentage
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  // Formatter for displayed amounts
  const fmt = useMemo(() => (v: number) => isNaN(v) ? "N/A" : v.toFixed(4), []);

  // Complement ratio for second token
  const ratioX2 = 1 - conversionRatio;
  // Dynamic track background
  const dynamicGradient = `linear-gradient(to right, #032c58 ${
    conversionRatio * 100
  }%, #b30202 ${conversionRatio * 100}%)`;

  return (
    <motion.div
      className="p-3 bg-white/20 backdrop-blur-md shadow-2xl space-y-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Slider section with tooltip */}
      <div className="flex flex-col items-center w-full max-w-xl mx-auto space-y-2">
        <div className="relative w-full">
          {/* Show hover tooltip */}
          {hoverValue !== null && <div>{/* {hoverValue}% */}</div>}
          <p className="text-center font-bold text-gray-700 mb-2">
            Use the slider to set how your NAU is split between NAUY and NAUY.
          </p>
          <Slider.Root
            className="relative flex items-center select-none touch-none w-full h-6"
            value={[conversionRatio * 100]}
            onValueChange={(value) => {
              onRatioChange(value[0] / 100);
              setHoverValue(value[0]);
            }}
            onValueCommit={() => setHoverValue(null)}
            max={100}
            step={1}
            disabled={loading}
          >
            <Slider.Track
              className="relative grow rounded-full h-6"
              style={{ background: dynamicGradient }}
            >
              <Slider.Range className="absolute h-full" />
            </Slider.Track>
            <Slider.Thumb className="block w-6 h-6 bg-white border-2 border-blue-500 hover:border-red-600 rounded-full transition-transform transform hover:scale-125" />
          </Slider.Root>
        </div>

        {/* Display token percentages */}
        <div className="flex justify-between mt-2 text-[12px] uppercase font-semibold w-full px-2">
          <div className="inline-flex flex-col items-center gap-2 text-blue-600/80">
            <img
              src={logoNo}
              alt="NAUN Token"
              className="w-14 h-14 rounded-full"
            />
            <span className="inline-flex items-center justify-center gap-1 px-4 py-1.5 rounded-full bg-gradient-to-r from-red-500 to-red-900 text-white text-[11px] font-semibold shadow-lg uppercase w-24 text-center">
              NAUN: {Math.round(ratioX2 * 100)}%
            </span>
          </div>

          <div className="inline-flex items-center flex-col gap-2 text-red-600/80">
            <img
              src={logoYes}
              alt="NAUY Token"
              className="w-14 h-14 rounded-full"
            />
            <span className="inline-flex items-center justify-center gap-1 px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-600 to-blue-900 text-white text-[11px] font-semibold shadow-lg uppercase w-24 text-center">
              NAUY: {Math.round(conversionRatio * 100)}%
            </span>
          </div>
        </div>
      </div>

      {/* Converted amounts section */}
      <div className="space-y-4">
        {loading ? (
          // SkeleNAUN loaders when fetching
          <>
            <div className="h-4 bg-gray-400/30 rounded animate-pulse" />
            <div className="h-4 bg-gray-400/30 rounded animate-pulse" />
          </>
        ) : (
          <AnimatePresence>
            <p className="text-center font-bold text-gray-800">
              These values update in real-time based on the amount and ratio you
              choose.{" "}
            </p>
            {[
              { amt: convertedAmountNAUY, label: "Minted NAUY" },
              { amt: convertedAmountNAUN, label: "Minted NAUN" },
            ].map(({ amt, label }, idx) => (
              <motion.div
                key={idx}
                className="flex justify-between text-white font-semibold"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={rowVariants}
                custom={idx}
                whileHover={{ scale: 1.02 }}
              >
                <span>{label}:</span>
                <motion.span
                  key={idx}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={idx === 0 ? "text-blue-800" : "text-red-700"}
                >
                  {fmt(amt)} {idx === 0 ? "NAUY" : "NAUN"}
                </motion.span>
              </motion.div>
            ))}
          </AnimatePresence>
        )}

        {/* Display error message if any */}
        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-400 text-center font-medium mt-2"
          >
            {error}
          </motion.p>
        )}
      </div>
    </motion.div>
  );
};

export default CombinedConverter;
